import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiPackageVariantClosed } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { CreateImportDialog } from './CreateImportDialog'
import type { ImportRow } from './importTypes'

/** Radix Select forbids an empty string value, so "no import" needs a sentinel. */
const NONE_VALUE = '__none__'
/** Sentinel for the inline "+ New import" entry at the bottom of the list. */
const NEW_VALUE = '__new__'

interface ImportSelectProps {
    org: string
    /** Selected import UID, or '' for none. */
    value: string
    onChange: (importUid: string) => void
    /**
     * Locked selection (spec §3): the import page's own deposit zone shows
     * which import it feeds but does not let the user retarget it.
     */
    locked?: boolean
    className?: string
    /** Prefix for the data-testids, so several selectors can coexist on a page. */
    testidPrefix?: string
}

/**
 * Optional import selector for the document upload surfaces (spec
 * 2026-08-16-05 §2).
 *
 * Lists the organization's `running` imports newest first, defaults to
 * **None**, and carries a "+ New import" entry at the bottom that declares one
 * inline and selects it — so "declare then deposit" is a single gesture.
 *
 * The selection is deliberately plain component state: it is a property of the
 * *batch being uploaded*, not of the user, and persisting it would silently
 * attach next week's upload to a forgotten deposit.
 */
export function ImportSelect({
    org,
    value,
    onChange,
    locked = false,
    className,
    testidPrefix = 'import-select',
}: ImportSelectProps) {
    const { t } = useTranslation('imports')
    const [createOpen, setCreateOpen] = useState(false)

    const { data } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/imports',
        { params: { path: { orgCode: org }, query: { limit: 200 } } },
    )

    const imports = useMemo(() => {
        const rows = ((data as any)?.data ?? []) as ImportRow[]
        // Only an OPEN deposit may receive new documents — a completed or
        // cancelled one would either re-open history or be refused with a 404
        // by the import middleware. The currently selected import is kept in
        // the list even if it left `running` meanwhile, so the trigger never
        // renders a blank label for a value we are actually sending.
        const running = rows
            .filter(row => row.status === 'running')
            .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime())
        if (value && !running.some(row => row.uid === value)) {
            const selected = rows.find(row => row.uid === value)
            if (selected) return [selected, ...running]
        }
        return running
    }, [data, value])

    const handleChange = (next: string) => {
        if (next === NEW_VALUE) {
            setCreateOpen(true)
            return
        }
        onChange(next === NONE_VALUE ? '' : next)
    }

    return (
        <>
            <Select value={value || NONE_VALUE} onValueChange={handleChange} disabled={locked}>
                <SelectTrigger className={className} data-testid={testidPrefix}>
                    <span className="flex items-center gap-2 truncate">
                        <AppIcon tone="muted" path={mdiPackageVariantClosed} size="sm" className="shrink-0" />
                        <SelectValue placeholder={t('select.none')} />
                    </span>
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value={NONE_VALUE} data-testid={`${testidPrefix}-none`}>
                        {t('select.none')}
                    </SelectItem>
                    {imports.map(imp => (
                        <SelectItem key={imp.uid} value={imp.uid} data-testid={`${testidPrefix}-option-${imp.uid}`}>
                            {imp.label}
                        </SelectItem>
                    ))}
                    <SelectItem value={NEW_VALUE} data-testid={`${testidPrefix}-new`}>
                        {t('select.new')}
                    </SelectItem>
                </SelectContent>
            </Select>

            <CreateImportDialog
                org={org}
                open={createOpen}
                onOpenChange={setCreateOpen}
                onCreated={created => onChange(created.uid)}
            />
        </>
    )
}

/**
 * Labelled wrapper used by the upload panels: the "Import" caption plus the
 * selector, so the three surfaces render it identically.
 */
export function ImportSelectField(props: ImportSelectProps) {
    const { t } = useTranslation('imports')

    return (
        <div className="space-y-1">
            <label className="block text-sm font-medium text-foreground">{t('select.title')}</label>
            <ImportSelect {...props} />
            <p className="text-xs text-muted-foreground">{t('select.hint')}</p>
        </div>
    )
}
